import { Fragment, useEffect, useRef, useState } from "react";
import { Menu, Portal } from "@headlessui/react";
import { ChevronDown, Trash2, type LucideIcon } from "lucide-react";

export interface RowActionItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
}

interface RowActionsProps {
  items: RowActionItem[];
  onDelete?: () => void;
  deleteTitle?: string;
  deleteHidden?: boolean;
}

const MENU_W = 224;

/**
 * Per-rij actie-dropdown. Het menu wordt in een Portal met position:fixed
 * gerenderd, zodat het niet wordt afknapt door overflow-containers (zoals de
 * tabel-wrapper) én altijd zichtbaar boven de inhoud ligt. Delete is een
 * aparte icoon naast de dropdown.
 */
export function RowActions({
  items,
  onDelete,
  deleteTitle = "Verwijderen",
  deleteHidden = false,
}: RowActionsProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const visible = items.filter((i) => !i.hidden);

  return (
    <Menu as={Fragment}>
      {({ open, close }) => (
        <div className="flex items-center gap-1">
          <ScrollCloser open={open} close={close} />
          <div className="relative inline-block">
            <Menu.Button
              ref={btnRef}
              onClick={() => {
                const r = btnRef.current?.getBoundingClientRect();
                if (r) {
                  const left = Math.min(r.right - MENU_W, window.innerWidth - MENU_W - 8);
                  setCoords({ top: r.bottom + 4, left: Math.max(8, left) });
                }
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-brand-600 shadow-sm hover:bg-brand-50 hover:border-brand-300"
              title="Acties"
            >
              Acties
              <ChevronDown size={15} className={`text-brand-600 transition-transform ${open ? "rotate-180" : ""}`} />
            </Menu.Button>

            {open && (
              <Portal>
                <Menu.Items
                  style={{ position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`, zIndex: 50 }}
                  className="w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-xl focus:outline-none"
                >
                  {visible.map((item, idx) => (
                    <Menu.Item key={idx} disabled={item.disabled}>
                      {({ active, disabled }) => (
                        <button
                          onClick={() => {
                            close();
                            item.onClick();
                          }}
                          disabled={disabled}
                          className={`${active ? "bg-brand-50 text-brand-700" : "text-slate-700"} ${
                            disabled ? "opacity-40 cursor-not-allowed" : ""
                          } flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left`}
                        >
                          {item.icon && <item.icon size={17} />}
                          {item.label}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Portal>
            )}
          </div>

          {onDelete && !deleteHidden && (
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600"
              title={deleteTitle}
            >
              <Trash2 size={20} />
            </button>
          )}
        </div>
      )}
    </Menu>
  );
}

/** Sluit het menu bij scrollen (fixed menu zou anders loskomen van de knop). */
function ScrollCloser({ open, close }: { open: boolean; close: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onScroll = () => close();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open, close]);
  return null;
}